import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2, ChevronLeft, Paperclip, MessageSquare, Send, CheckCircle } from 'lucide-react';
import { adminInquiryApi, type AdminInquiryDetail as AdminInquiryDetailType, InquiryStatus } from '@/api/inquiryApi';

const STATUS_LABEL: Record<InquiryStatus, string> = {
  PENDING: '답변대기',
  ANSWERED: '답변완료',
  CLOSED: '완료',
};

const STATUS_CLASS: Record<InquiryStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  ANSWERED: 'bg-green-100 text-green-700 border-green-300',
  CLOSED: 'bg-gray-100 text-gray-500 border-gray-300',
};

export default function AdminInquiryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inquiry, setInquiry] = useState<AdminInquiryDetailType | null>(null);
  const [answerContent, setAnswerContent] = useState('');
  const [answerEdit, setAnswerEdit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [closing, setClosing] = useState(false);

  const MIN_ANSWER = 5;
  const MAX_ANSWER = 3000;

  const load = () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    adminInquiryApi.getDetail(Number(id))
      .then(res => {
        setInquiry(res.data);
        if (res.data.answer) {
          setAnswerContent(res.data.answer.content);
        }
      })
      .catch(() => setError('문의 내용을 불러오는데 실패했습니다.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const handleAnswerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inquiry) return;
    if (!answerContent.trim() || answerContent.trim().length < MIN_ANSWER) {
      setError(`답변은 ${MIN_ANSWER}자 이상 입력해주세요.`);
      return;
    }
    if (answerContent.trim().length > MAX_ANSWER) {
      setError(`답변은 ${MAX_ANSWER}자 이하여야 합니다.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (inquiry.answer) {
        await adminInquiryApi.updateAnswer(inquiry.id, { content: answerContent.trim() });
      } else {
        await adminInquiryApi.createAnswer(inquiry.id, { content: answerContent.trim() });
      }
      setAnswerEdit(false);
      load();
    } catch {
      setError('답변 등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async () => {
    if (!inquiry) return;
    if (!window.confirm('이 문의를 완료 처리하시겠습니까?')) return;
    setClosing(true);
    try {
      await adminInquiryApi.updateStatus(inquiry.id, 'CLOSED');
      load();
    } catch {
      setError('상태 변경에 실패했습니다.');
    } finally {
      setClosing(false);
    }
  };

  const formatDateTime = (iso: string) =>
    iso ? new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    }) : '-';

  if (loading) {
    return (
      <div className="flex justify-center items-center py-40">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !inquiry) {
    return (
      <div className="p-4 md:p-8 max-w-3xl mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!inquiry) return null;

  return (
    <div className="space-y-6 p-4 md:p-8 max-w-3xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold">1:1 문의 상세</h1>
        {inquiry.status === 'ANSWERED' && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleClose}
            disabled={closing}
            className="text-gray-600"
          >
            {closing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
            완료 처리
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* 문의자 정보 */}
      <Card className="bg-gray-50/50">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap gap-4 text-sm">
            <div><span className="text-gray-400">사용자: </span><span className="text-gray-800 font-medium">{inquiry.userEmail}</span></div>
            {inquiry.userName && <div><span className="text-gray-400">이름: </span><span className="text-gray-700">{inquiry.userName}</span></div>}
            <div className="ml-auto">
              <Badge className={STATUS_CLASS[inquiry.status]}>
                {STATUS_LABEL[inquiry.status]}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 문의 본문 */}
      <Card>
        <CardHeader className="border-b pb-4">
          <div className="text-xs text-gray-400 mb-1">{formatDateTime(inquiry.createdAt)}</div>
          <CardTitle className="text-lg leading-relaxed">
            {inquiry.isSecret && <span className="text-gray-400 mr-2">🔒</span>}
            {inquiry.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-5">
          <div className="whitespace-pre-wrap text-gray-800 leading-relaxed text-sm" style={{ minHeight: '100px' }}>
            {inquiry.content}
          </div>
          {inquiry.attachmentUrl && (
            <div className="mt-5 pt-4 border-t">
              <p className="text-xs text-gray-400 mb-2 font-medium">첨부파일</p>
              <a
                href={inquiry.attachmentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <Paperclip className="w-3.5 h-3.5" />
                {inquiry.attachmentName || '첨부파일 다운로드'}
              </a>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 답변 영역 */}
      <Card className={inquiry.answer ? 'border-blue-200' : 'border-dashed border-gray-200'}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-blue-600" />
              <CardTitle className="text-base text-blue-700">관리자 답변</CardTitle>
            </div>
            {inquiry.answer && !answerEdit && inquiry.status !== 'CLOSED' && (
              <Button variant="outline" size="sm" onClick={() => setAnswerEdit(true)}>
                수정
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!inquiry.answer || answerEdit ? (
            <form onSubmit={handleAnswerSubmit} className="space-y-3">
              <div className="flex items-end justify-between">
                <label className="text-xs text-gray-500">
                  {!inquiry.answer ? '답변을 작성해주세요.' : '답변을 수정합니다.'}
                </label>
                <span className={`text-xs ${answerContent.length > MAX_ANSWER ? 'text-red-500' : 'text-gray-400'}`}>
                  {answerContent.length}/{MAX_ANSWER}
                </span>
              </div>
              <textarea
                value={answerContent}
                onChange={e => setAnswerContent(e.target.value)}
                rows={8}
                maxLength={MAX_ANSWER}
                placeholder={`답변 내용을 ${MIN_ANSWER}자 이상 작성해주세요.`}
                disabled={submitting || inquiry.status === 'CLOSED'}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
              <div className="flex justify-end gap-2">
                {answerEdit && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { setAnswerEdit(false); setAnswerContent(inquiry.answer?.content || ''); }}
                    disabled={submitting}
                  >
                    취소
                  </Button>
                )}
                <Button type="submit" size="sm" disabled={submitting || inquiry.status === 'CLOSED'}>
                  {submitting ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />등록 중...</>
                  ) : (
                    <><Send className="w-3.5 h-3.5 mr-1.5" />{inquiry.answer ? '답변 수정' : '답변 등록'}</>
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <div>
              <div className="whitespace-pre-wrap text-gray-800 leading-relaxed text-sm">
                {inquiry.answer.content}
              </div>
              <div className="text-xs text-gray-400 mt-3">{formatDateTime(inquiry.answer.createdAt)}</div>
            </div>
          )}
        </CardContent>
      </Card>

      <div>
        <Button variant="outline" onClick={() => navigate('/dashboard/admin/inquiries')}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          목록으로
        </Button>
      </div>
    </div>
  );
}
